import { Quote } from 'lucide-react'
import { ComponentProps } from 'react'
import { twMerge } from 'tailwind-merge'
import { tv, VariantProps } from 'tailwind-variants'

import { createMarkup } from '@/helper/create-markup'
import { Testimonial } from '@/models/testimonial'

import { Avatar, AvatarFallback, AvatarImage } from '../avatar'
import { ScrollArea } from '../scroll-area'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../tooltip'

const testimonialVariant = tv({
  base: '',
  variants: {
    variant: {
      default: 'w-full md:w-md 2xl:w-lg mx-auto',
      slider: 'w-full md:w-[90%] md:ml-auto',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

interface TestimonialsCardProps
  extends ComponentProps<'div'>,
    VariantProps<typeof testimonialVariant> {
  testimonial: Testimonial
  handleOpenModal: (testimonial: Testimonial) => void
}

export function TestimonialsCard({
  variant,
  className,
  testimonial,
  handleOpenModal,
}: TestimonialsCardProps) {
  return (
    <div className={twMerge('group h-74.75 w-full', className)}>
      <div
        onClick={() => handleOpenModal(testimonial)}
        className={testimonialVariant({
          variant,
          className: twMerge(
            'relative h-full cursor-pointer bg-white',
            'rounded-tl-2xl rounded-br-2xl',
            'shadow-xs transition-shadow group-hover:shadow-md',
            'border-bios-primary border-b-4 border-l-0 md:border-r-0 md:border-l-4',
          ),
        })}
      >
        <div>
          <Avatar
            title={testimonial.nome_pessoa}
            className="absolute size-24 rounded-none select-none md:top-1/2 md:-left-12 md:size-32 md:-translate-y-1/2"
          >
            <AvatarImage
              src={testimonial.foto_pessoa}
              alt={`Photo profile of ${testimonial.nome_pessoa}`}
              className="rounded-tl-2xl rounded-br-2xl"
            />
            <AvatarFallback className="rounded-none rounded-tl-2xl rounded-br-2xl bg-zinc-300 font-bold">
              {testimonial.nome_pessoa[0].toUpperCase()}
            </AvatarFallback>
          </Avatar>

          <Quote className="fill-bios-primary/20 absolute top-px right-2 size-12 text-transparent md:size-14 2xl:size-16" />
        </div>

        <div className="absolute bottom-2 flex w-full flex-col gap-3 p-4 md:inset-0 md:ml-14 md:w-[calc(100%-5rem)] md:px-10 md:py-4 2xl:w-[calc(100%-6rem)]">
          <div className="text-bios-title">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <h3 className="truncate font-bold">
                    {testimonial.nome_pessoa}
                  </h3>
                </TooltipTrigger>
                <TooltipContent isWithPortal align="start">
                  {testimonial.nome_pessoa}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <p className="text-sm">{testimonial.descricao_pessoa}</p>
          </div>

          <ScrollArea className="h-24 pr-3 md:h-48">
            <div
              className="text-monochromatic-500 break-all whitespace-pre-wrap"
              dangerouslySetInnerHTML={createMarkup(testimonial.depoimento)}
            />
          </ScrollArea>
        </div>
      </div>
    </div>
  )
}
